"use client";

import { useContext, useEffect, useRef, useState, type ReactNode } from "react";

import { RegistrationDialogReadiness } from "@/features/login/components/RegistrationDialog";

const returnKey = "mira-registration-face-return";
/** A draft belongs to one document, not a persistent login or browser tab. */
export function RegistrationLifecycle({ children, faceReturn = false, restart = false, restartHref = "/register" }: { children: ReactNode; faceReturn?: boolean; restart?: boolean; restartHref?: string }) {
  const setDialogReady = useContext(RegistrationDialogReadiness);
  const token = useRef("");
  const leaving = useRef(false);
  const cleanup = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    setDialogReady?.(ready || Boolean(error));
  }, [ready, error, setDialogReady]);
  useEffect(() => {
    clearTimeout(cleanup.current);
    if (!token.current) {
      let returning: string | null = null;
      try {
        if (faceReturn) returning = sessionStorage.getItem(returnKey);
        sessionStorage.removeItem(returnKey);
      } catch { /* A fresh document will safely restart if storage is unavailable. */ }
      token.current = returning || crypto.randomUUID();
    }
    let active = true;
    let entered = false;
    const leave = () => {
      if (!entered || leaving.current) return;
      const body = JSON.stringify({ action: "leave", token: token.current });
      void fetch("/api/onboarding/registration", { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    };
    const handoff = () => {
      try { sessionStorage.setItem(returnKey, token.current); leaving.current = true; }
      catch { /* Do not silently turn unavailable storage into a permanent session. */ }
    };
    const restore = (event: PageTransitionEvent) => { if (event.persisted) window.location.reload(); };
    window.addEventListener("pageshow", restore);
    window.addEventListener("pagehide", leave);
    window.addEventListener("mira-registration-face-handoff", handoff);
    void (async () => {
      try {
        const response = await fetch("/api/onboarding/registration", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: restart ? "restart" : "enter", token: token.current }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "暂时无法核对注册状态");
        if (!active) return;
        if (result.restart) { window.location.replace(restartHref); return; }
        entered = !result.retained;
        setReady(true);
      } catch (cause) { if (active) setError(cause instanceof Error ? cause.message : "暂时无法核对注册状态"); }
    })();
    return () => {
      active = false;
      window.removeEventListener("pageshow", restore);
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("mira-registration-face-handoff", handoff);
      cleanup.current = setTimeout(leave, 0);
    };
  }, [faceReturn, retry, restart, restartHref]);
  if (error) return <div role="alert"><p>{error}</p><button type="button" onClick={() => { setError(""); setRetry(value => value + 1); }}>重试</button></div>;
  return ready ? children : null;
}
